import React from 'react';
import { observer } from 'mobx-react';

import { GridEventListener } from '@mui/x-data-grid';

import { reverse } from 'named-urls';

import { Button } from 'components/Button';

import { useStore } from 'utils/hooks/store';

import Grid from 'components/Grid';

import { getEntityTranslation } from 'stores/EntityDetails/utils';

import routes from 'core/routes';

import { EntityModules, isRegisterEntityModule } from 'core/constants';

import { EntityRelatedTabProps } from 'components/Entities/types';

import DataError from 'containers/Error/DataError';

import { getBasicColumnsBasedOnEntity } from 'components/Entities/EntityRelatedPage/RelatedEntities/utils';

import AbstractWrapper from '../AbstractWrapper';
import { defaultProps } from '../types';

const EntitiesRelationWrapper: React.FunctionComponent<EntityRelatedTabProps & { module: EntityModules }> = ({
  onError,
  store,
  module,
}) => {
  const { routing } = useStore();

  const { details, entityType } = store;
  const { plural, key } = getEntityTranslation[module];

  // Audits are excluded too — they have no `details` route to reverse into.
  if (!isRegisterEntityModule(module)) return <DataError />;

  const redirectOnClick: GridEventListener<'rowClick'> = (entity): void =>
    routing.push(reverse(routes.dashboard[module].details, { entityId: entity.row.uuid }));

  const redirectToAll = (): void => {
    const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
    routing.push(
      // @ts-ignore
      reverse(routes.dashboard[entityType][`related${capitalizedKey}`], {
        entityId: details?.uuid || '',
      })
    );
  };

  const basicColumns =
    module === EntityModules.Events
      ? // for events module do not display owner, related actions and createdAt
        getBasicColumnsBasedOnEntity(module).filter(
          (column) =>
            column.field !== 'events__owner' && column.field !== 'relatedActions' && column.field !== 'createdAt'
        )
      : getBasicColumnsBasedOnEntity(module);

  return (
    <AbstractWrapper
      key={`module-${module}`}
      headerTitle={plural}
      gridElement={
        <Grid
          onError={onError}
          onRowClick={redirectOnClick}
          basicColumns={basicColumns}
          dataURL={`/api/entities/${entityType}/${details?.uuid}/${module}/`}
          fieldsURL={`/api/entities/${module}/fields/`}
          {...defaultProps}
        />
      }
      footerAction={
        <Button variant="text" size="medium" onClick={redirectToAll}>
          View all...
        </Button>
      }
    />
  );
};

const EntitiesRelatedToEntities: React.FunctionComponent<EntityRelatedTabProps> = ({ onError, store }) => {
  const {
    session: { user },
  } = useStore();
  if (!user) return <DataError />;

  const relatedEntities = store.details?.related || [];
  const commonElements = relatedEntities.filter((element) => user.activeEntityModules.includes(element));

  return commonElements.map((module) => <EntitiesRelationWrapper onError={onError} store={store} module={module} />);
};

export default observer(EntitiesRelatedToEntities);
